// src/components/ConfirmSignUp.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CognitoUser } from "amazon-cognito-identity-js";
import { userPool } from "../awsConfig";

const ConfirmSignUp = () => {
  const [formData, setFormData] = useState({
    username: "",
    confirmationCode: "",
  });
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Auto-populate username from localStorage
  useEffect(() => {
    const pendingUsername = localStorage.getItem("pendingUsername");
    if (pendingUsername) {
      setFormData((prev) => ({
        ...prev,
        username: pendingUsername,
      }));
    }
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Handle confirmation submission
  const handleConfirm = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    const { username, confirmationCode } = formData;

    if (!username || !confirmationCode) {
      setMessage("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
      setIsLoading(false);

      if (err) {
        console.error("Confirmation error:", err);

        let errorMessage = "Confirmation failed. ";

        switch (err.code) {
          case "CodeMismatchException":
            errorMessage +=
              "Invalid confirmation code. Please check your email and try again.";
            break;
          case "ExpiredCodeException":
            errorMessage +=
              "Confirmation code has expired. Please request a new one.";
            break;
          case "NotAuthorizedException":
            errorMessage += "User is already confirmed or does not exist.";
            break;
          default:
            errorMessage += err.message;
        }

        setMessage(errorMessage);
        return;
      }

      console.log("Confirmation successful:", result);
      setMessage("Registration confirmed successfully! You can now log in.");

      // Clear pending username from localStorage
      localStorage.removeItem("pendingUsername");

      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-3xl font-extrabold text-center text-accent">
          Confirm Registration
        </h2>
        <form onSubmit={handleConfirm} className="space-y-6">
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-textPrimary"
            >
              Username
            </label>
            <input
              type="text"
              name="username"
              id="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent"
              required
            />
          </div>
          <div>
            <label
              htmlFor="confirmationCode"
              className="block text-sm font-medium text-textPrimary"
            >
              Confirmation Code
            </label>
            <input
              type="text"
              name="confirmationCode"
              id="confirmationCode"
              value={formData.confirmationCode}
              onChange={handleChange}
              className="w-full px-3 py-2 mt-1 border rounded-md focus:ring-accent focus:border-accent"
              required
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full px-4 py-2 font-semibold text-white bg-accent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent ${
                isLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-accent-dark"
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Confirming...
                </span>
              ) : (
                "Confirm Registration"
              )}
            </button>
          </div>
        </form>
        {message && <p className="text-sm text-red-600">{message}</p>}
      </div>
    </div>
  );
};

export default ConfirmSignUp;
